/**
 * TransportManager.ts - Handles ship, canoe, and airship transport
 *
 * Transport progression in FF1:
 * 1. Ship - Obtained after defeating pirates in Pravoka
 * 2. Canoe - Obtained from Circle of Sages at Crescent Lake
 * 3. Airship - Obtained by using Levistone at desert altar
 */

import { TerrainType } from './data/TilesetConfig';

export enum TransportType {
  NONE = 'NONE',
  SHIP = 'SHIP',
  CANOE = 'CANOE',
  AIRSHIP = 'AIRSHIP',
}

export interface TransportState {
  // What transports the player has unlocked
  hasShip: boolean;
  hasCanoe: boolean;
  hasAirship: boolean;

  // Current transport being used
  currentTransport: TransportType;

  // Ship location when not riding (null if player is on ship)
  shipLocation: { x: number; y: number } | null;

  // Airship location when not riding (null if player is on airship)
  airshipLocation: { x: number; y: number } | null;
}

export class TransportManager {
  private state: TransportState;

  // Callbacks for scene updates
  private onTransportChange: ((transport: TransportType) => void) | null = null;

  constructor() {
    this.state = {
      hasShip: false,
      hasCanoe: false,
      hasAirship: false,
      currentTransport: TransportType.NONE,
      shipLocation: null,
      airshipLocation: null,
    };
  }

  // =========================================================================
  // Transport Acquisition
  // =========================================================================

  /**
   * Grant the ship to the player (after defeating pirates)
   * Ship spawns at Pravoka dock
   */
  grantShip(dockX: number = 8, dockY: number = 28): void {
    if (!this.state.hasShip) {
      console.log('[TransportManager] Ship acquired! Docked at:', dockX, dockY);
      this.state.hasShip = true;
      this.state.shipLocation = { x: dockX, y: dockY };
    }
  }

  /**
   * Grant the canoe to the player (from Sages at Crescent Lake)
   * Canoe doesn't have a fixed location - auto-equips on rivers
   */
  grantCanoe(): void {
    if (!this.state.hasCanoe) {
      console.log('[TransportManager] Canoe acquired!');
      this.state.hasCanoe = true;
    }
  }

  /**
   * Grant the airship to the player (Levistone at desert altar)
   * Airship spawns at the desert altar location
   */
  grantAirship(altarX: number = 28, altarY: number = 44): void {
    if (!this.state.hasAirship) {
      console.log('[TransportManager] Airship acquired! Location:', altarX, altarY);
      this.state.hasAirship = true;
      this.state.airshipLocation = { x: altarX, y: altarY };
    }
  }

  // =========================================================================
  // Transport State Queries
  // =========================================================================

  hasShip(): boolean {
    return this.state.hasShip;
  }

  hasCanoe(): boolean {
    return this.state.hasCanoe;
  }

  hasAirship(): boolean {
    return this.state.hasAirship;
  }

  getCurrentTransport(): TransportType {
    return this.state.currentTransport;
  }

  isOnTransport(): boolean {
    return this.state.currentTransport !== TransportType.NONE;
  }

  getShipLocation(): { x: number; y: number } | null {
    return this.state.shipLocation;
  }

  getAirshipLocation(): { x: number; y: number } | null {
    return this.state.airshipLocation;
  }

  // =========================================================================
  // Boarding and Disembarking
  // =========================================================================

  /**
   * Check if player can board a transport at the given position
   */
  canBoard(playerX: number, playerY: number, terrain: TerrainType): TransportType | null {
    // Check for ship - player must be on SHORE tile adjacent to ship
    if (this.state.hasShip && this.state.shipLocation) {
      const ship = this.state.shipLocation;
      const isAdjacent = Math.abs(playerX - ship.x) + Math.abs(playerY - ship.y) <= 1;
      if (isAdjacent && terrain === TerrainType.SHORE) {
        return TransportType.SHIP;
      }
    }

    // Check for airship - player must be on same tile as airship
    if (this.state.hasAirship && this.state.airshipLocation) {
      const airship = this.state.airshipLocation;
      if (playerX === airship.x && playerY === airship.y) {
        return TransportType.AIRSHIP;
      }
    }

    // Canoe auto-boards when entering river from shore (handled in movement)
    return null;
  }

  /**
   * Board a transport
   */
  board(transport: TransportType, playerX: number, playerY: number): boolean {
    if (this.state.currentTransport !== TransportType.NONE) {
      console.log('[TransportManager] Already on a transport!');
      return false;
    }

    switch (transport) {
      case TransportType.SHIP:
        if (!this.state.hasShip || !this.state.shipLocation) return false;
        console.log('[TransportManager] Boarding ship');
        this.state.currentTransport = TransportType.SHIP;
        this.state.shipLocation = null; // Ship moves with player
        break;

      case TransportType.CANOE:
        if (!this.state.hasCanoe) return false;
        console.log('[TransportManager] Using canoe');
        this.state.currentTransport = TransportType.CANOE;
        break;

      case TransportType.AIRSHIP:
        if (!this.state.hasAirship || !this.state.airshipLocation) return false;
        console.log('[TransportManager] Boarding airship');
        this.state.currentTransport = TransportType.AIRSHIP;
        this.state.airshipLocation = null; // Airship moves with player
        break;

      default:
        return false;
    }

    if (this.onTransportChange) {
      this.onTransportChange(this.state.currentTransport);
    }
    return true;
  }

  /**
   * Disembark from current transport
   */
  disembark(playerX: number, playerY: number, terrain: TerrainType): boolean {
    const current = this.state.currentTransport;
    if (current === TransportType.NONE) return false;

    // Check if disembarking is allowed on this terrain
    switch (current) {
      case TransportType.SHIP:
        // Can only disembark ship on SHORE tiles
        if (terrain !== TerrainType.SHORE) {
          console.log('[TransportManager] Cannot disembark ship here - need shore');
          return false;
        }
        console.log('[TransportManager] Disembarking ship at', playerX, playerY);
        this.state.shipLocation = { x: playerX, y: playerY };
        break;

      case TransportType.CANOE:
        // Can only disembark canoe on SHORE or land tiles adjacent to river
        if (!this.isLandTerrain(terrain)) {
          console.log('[TransportManager] Cannot leave canoe here');
          return false;
        }
        console.log('[TransportManager] Leaving canoe');
        break;

      case TransportType.AIRSHIP:
        // Can only land airship on GRASS or DESERT
        if (terrain !== TerrainType.GRASS && terrain !== TerrainType.DESERT) {
          console.log('[TransportManager] Cannot land airship here - need grass or desert');
          return false;
        }
        console.log('[TransportManager] Landing airship at', playerX, playerY);
        this.state.airshipLocation = { x: playerX, y: playerY };
        break;
    }

    this.state.currentTransport = TransportType.NONE;
    if (this.onTransportChange) {
      this.onTransportChange(TransportType.NONE);
    }
    return true;
  }

  // =========================================================================
  // Movement Validation
  // =========================================================================

  /**
   * Check if player can move to the target terrain with current transport
   */
  canMoveTo(terrain: TerrainType): boolean {
    switch (this.state.currentTransport) {
      case TransportType.NONE:
        // On foot - use normal walkability
        return this.isLandWalkable(terrain);

      case TransportType.SHIP:
        // Ship can move on OCEAN, DEEP_WATER, SHORE
        return terrain === TerrainType.OCEAN ||
               terrain === TerrainType.DEEP_WATER ||
               terrain === TerrainType.SHORE;

      case TransportType.CANOE:
        // Canoe can move on RIVER, SHORE (and can exit to land)
        return terrain === TerrainType.RIVER ||
               terrain === TerrainType.SHORE ||
               this.isLandWalkable(terrain);

      case TransportType.AIRSHIP:
        // Airship can fly over anything (will land check separately)
        return true;

      default:
        return false;
    }
  }

  /**
   * Check if terrain is walkable on foot
   */
  private isLandWalkable(terrain: TerrainType): boolean {
    switch (terrain) {
      case TerrainType.GRASS:
      case TerrainType.GRASS_FLOWERS:
      case TerrainType.FOREST:
      case TerrainType.DESERT:
      case TerrainType.SWAMP:
      case TerrainType.ROAD:
      case TerrainType.BRIDGE:
      case TerrainType.SHORE:
      case TerrainType.SNOW:
      case TerrainType.TOWN:
      case TerrainType.CASTLE:
      case TerrainType.CAVE:
      case TerrainType.SHRINE:
        return true;
      default:
        return false;
    }
  }

  /**
   * Check if terrain is a land type (not water)
   */
  private isLandTerrain(terrain: TerrainType): boolean {
    return terrain !== TerrainType.OCEAN &&
           terrain !== TerrainType.DEEP_WATER &&
           terrain !== TerrainType.RIVER &&
           terrain !== TerrainType.LAVA;
  }

  /**
   * Handle automatic canoe boarding/disembarking
   * Returns true if transport state changed
   */
  handleCanoeTransition(fromTerrain: TerrainType, toTerrain: TerrainType): boolean {
    if (!this.state.hasCanoe) return false;

    // Auto-board canoe when stepping from shore into river
    if (this.state.currentTransport === TransportType.NONE) {
      if (fromTerrain === TerrainType.SHORE && toTerrain === TerrainType.RIVER) {
        this.state.currentTransport = TransportType.CANOE;
        console.log('[TransportManager] Auto-boarding canoe');
        if (this.onTransportChange) {
          this.onTransportChange(TransportType.CANOE);
        }
        return true;
      }
    }

    // Auto-disembark canoe when stepping from river to land
    if (this.state.currentTransport === TransportType.CANOE) {
      if (toTerrain !== TerrainType.RIVER && this.isLandTerrain(toTerrain)) {
        this.state.currentTransport = TransportType.NONE;
        console.log('[TransportManager] Auto-disembarking canoe');
        if (this.onTransportChange) {
          this.onTransportChange(TransportType.NONE);
        }
        return true;
      }
    }

    return false;
  }

  // =========================================================================
  // Callbacks
  // =========================================================================

  /**
   * Set callback for transport state changes
   */
  onTransportChanged(callback: (transport: TransportType) => void): void {
    this.onTransportChange = callback;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /**
   * Get state for saving
   */
  getState(): TransportState {
    return { ...this.state };
  }

  /**
   * Restore state from save
   */
  setState(state: Partial<TransportState>): void {
    this.state = {
      ...this.state,
      ...state,
    };
  }

  /**
   * Reset to initial state (for new game)
   */
  reset(): void {
    this.state = {
      hasShip: false,
      hasCanoe: false,
      hasAirship: false,
      currentTransport: TransportType.NONE,
      shipLocation: null,
      airshipLocation: null,
    };
  }

  // =========================================================================
  // Debug
  // =========================================================================

  /**
   * Grant all transports (for testing)
   */
  debugGrantAll(): void {
    this.grantShip(8, 28);
    this.grantCanoe();
    this.grantAirship(28, 44);
    console.log('[TransportManager] DEBUG: All transports granted');
  }
}
